import { useState, useEffect, useCallback } from 'react'
import { ListChecks, LogOut, Search, Users } from 'lucide-react'
import { fetchPersons, fetchStats, logout } from './lib/api'
import AddPersonForm from './components/AddPersonForm'
import PersonCard from './components/PersonCard'
import PersonDetail from './components/PersonDetail'
import ReviewView from './components/ReviewView'
import LoginView from './components/LoginView'

export default function App() {
  const [persons, setPersons] = useState([])
  const [stats, setStats] = useState({ total_persons: 0, by_status: {}, total_results: 0 })
  const [selectedId, setSelectedId] = useState(null)
  const [filter, setFilter] = useState('')
  const [activeView, setActiveView] = useState('persons')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const isLoginPage = window.location.pathname === '/login'

  const reload = useCallback(async () => {
    if (isLoginPage) return
    setError('')
    try {
      const [p, s] = await Promise.all([fetchPersons(), fetchStats()])
      setPersons(p)
      setStats(s)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [isLoginPage])

  useEffect(() => { reload() }, [reload])

  // ESC to close modal
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelectedId(null) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const filtered = filter
    ? persons.filter(p => {
        const q = filter.toLowerCase()
        return (p.first_name || '').toLowerCase().includes(q)
          || (p.last_name || '').toLowerCase().includes(q)
          || (p.nickname || '').toLowerCase().includes(q)
          || (p.city || '').toLowerCase().includes(q)
          || p.status === q
      })
    : persons

  const found = (stats.by_status?.gefunden || 0) + (stats.by_status?.kontaktiert || 0) + (stats.by_status?.zugesagt || 0)

  const handleLogout = async () => {
    try {
      await logout()
    } finally {
      window.location.assign('/login')
    }
  }

  if (isLoginPage) return <LoginView />

  return (
    <>
      <header className="hero">
        <div className="hero-inner">
          <span className="hero-badge">Runder Geburtstag</span>
          <button className="logout-button" onClick={handleLogout} type="button" aria-label="Abmelden">
            <LogOut size={15} />
          </button>
          <h1>Wiedersehen</h1>
          <p className="hero-sub">Alte Freunde finden. Neue Erinnerungen schaffen.</p>

          <div className="stats-row">
            <div className="stat">
              <span className="stat-num">{stats.total_persons}</span>
              <span className="stat-label">Personen</span>
            </div>
            <div className="stat">
              <span className="stat-num">{found}</span>
              <span className="stat-label">Gefunden</span>
            </div>
            <div className="stat">
              <span className="stat-num">{stats.by_status?.zugesagt || 0}</span>
              <span className="stat-label">Zugesagt</span>
            </div>
            <div className="stat">
              <span className="stat-num">{stats.total_results}</span>
              <span className="stat-label">Profile</span>
            </div>
          </div>
        </div>
      </header>

      <main className={`main ${activeView === 'review' ? 'main-review' : ''}`}>
        {error && (
          <div className="notice notice-error">
            <span>{error}</span>
            <button className="btn btn-ghost btn-sm" onClick={reload}>Erneut laden</button>
          </div>
        )}

        <div className="view-tabs" role="tablist" aria-label="Hauptansicht">
          <button
            className={`view-tab ${activeView === 'persons' ? 'active' : ''}`}
            onClick={() => setActiveView('persons')}
            type="button"
          >
            <Users size={15} /> Personen
          </button>
          <button
            className={`view-tab ${activeView === 'review' ? 'active' : ''}`}
            onClick={() => setActiveView('review')}
            type="button"
          >
            <ListChecks size={15} /> Review
          </button>
        </div>

        {activeView === 'persons' && <AddPersonForm onAdded={reload} />}

        {loading && (
          <div className="loading-card">
            <span className="spinner" />
            Lade Personen und Statistiken...
          </div>
        )}

        {!loading && activeView === 'persons' && persons.length > 3 && (
          <div style={{ marginTop: '0.8rem', position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute', left: '0.75rem', top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-muted)',
            }} />
            <input
              className="form-input"
              style={{ width: '100%', paddingLeft: '2rem' }}
              placeholder="Filtern nach Name, Stadt, Status..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
        )}

        {!loading && activeView === 'persons' && (
          <div className="person-list">
          {filtered.length === 0 && persons.length === 0 && (
            <div className="empty">
              <div className="empty-icon">🔍</div>
              <h3>Noch keine Personen</h3>
              <p>Füge Schulfreunde und Bekannte hinzu, die du wiederfinden möchtest.</p>
            </div>
          )}
          {filtered.length === 0 && persons.length > 0 && (
            <div className="empty">
              <p>Keine Treffer für "{filter}"</p>
            </div>
          )}
          {filtered.map(p => (
            <PersonCard key={p.id} person={p} onClick={setSelectedId} />
          ))}
          </div>
        )}

        {!loading && activeView === 'review' && (
          <ReviewView
            onOpenPerson={setSelectedId}
            onChanged={reload}
          />
        )}
      </main>

      <footer className="footer">
        Wiedersehen — nutzt nur öffentlich zugängliche Informationen (OSINT)<br />
        <small>Bitte beachte die DSGVO und nutze Ergebnisse nur für persönliche Zwecke.</small>
      </footer>

      {selectedId && (
        <PersonDetail
          personId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
        />
      )}
    </>
  )
}
